import { PlusOutlined, RightOutlined } from '@ant-design/icons';
import React, { memo, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import styled, { css } from 'styled-components';

import Accordion from '@/components/Accordion/Accordion';
import { CenterFlex, Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Menu from '@/components/Menu/Menu';
import Modal from '@/components/Modal/Modal';
import {
  default as Tooltip,
  default as TooltipV2,
} from '@/components/Tooltip/TooltipV2';
import Typo, { SpanStyle } from '@/components/Typography/Typo';
import {
  useFeedbackTerminology,
} from '@/entities/workspace';
import type { FeedbackCardDragItem } from '@/features/Taxonomy/components/FeedbackCard/FeedbackCard';
import PredictionStateDisplay from '@/features/Taxonomy/components/Topic2Detail/PredictionStateDisplay/PredictionStateDisplay';
import predictionStateUtils from '@/features/Taxonomy/components/Topic2Detail/PredictionStateDisplay/utils';
import { TopicTreeSortBy } from '@/features/Taxonomy/context/TaxonomyContext';
import { useTaxonomyDrag } from '@/features/Taxonomy/context/TaxonomyDragContext';
import { useTopicDetailContext } from '@/features/Taxonomy/context/TopicDetailContext';
import useTaxonomyTopicIcon from '@/features/Taxonomy/hooks/useTaxonomyTopicIcon';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services';
import { useToast } from '@/stores/pure/ToastStore';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import { TAXONOMY_DND_ITEM, TOPIC_2_TYPE } from '@/types/taxonomyConst';

import AISuggestionModal from '../TaxonomyDetail/AISuggestionModal/AISuggestionModal';
import SelectTopicTypeModal from '../TaxonomyDetail/CreateTopicModal/SelectTopicTypeModal';

const ShortTopicRow = <T extends TOPIC_2_TYPE>(props: {
  topic: Topic2TreeItemT<T>;
  onClick: (alias: string) => void;
  currentTopicAlias?: string;
  createTopicCallback?: (targetTopicAlias: string) => void;
  showPath?: boolean;
}) => {
  const { removeItem } = useTopicDetailContext();

  const addToast = useToast();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const dragContext = useTaxonomyDrag();
  const color = useColor();
  const isPrediction = predictionStateUtils.isPrediction(
    props.topic.prediction_state,
  );
  const topicTreeMap = useTaxonomyStore((state) => state.topicTreeMap);

  const toggleTopic = useTaxonomyStore((state) => state.toggleCollapseTopic);
  const getIcon = useTaxonomyTopicIcon();

  const {
    status: createModalStatus,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModalState();

  const {
    status: aiSuggestionModalStatus,
    open: openAiSuggestionModal,
    close: closeAiSuggestionModal,
  } = useModalState();

  const offset = (props.topic.indent - 1) * 37;

  const childTopics = useMemo(() => {
    const topics =
      props.topic.children?.reduce((acc, t) => {
        try {
          const topic = taxonomyUtils.getTaxonomyTopic(
            topicTreeMap,
            props.topic.taxonomyAlias,
            t,
          );
          if (topic) {
            acc.push(topic);
          }
        } catch (e) {
          return acc;
        }
        return acc;
      }, [] as Topic2TreeItemT<TOPIC_2_TYPE>[]) || [];

    return topics;
  }, [props.topic.children, props.topic.taxonomyAlias, topicTreeMap]);

  const childTopicType = useMemo(() => {
    return childTopics?.[0]?.category || null;
  }, [childTopics]);

  const focused = props.currentTopicAlias === props.topic.alias;

  const isValidDrop: boolean | 'START_TOPIC_IS_PARENT_OF_TARGET_TOPIC' =
    useMemo(() => {
      if (!dragContext.isDragging) return true;
      return taxonomyUtils.validateDropTopic({
        startTopicAlias: dragContext.isDragging.alias,
        isTaxonomy: dragContext.isDragging.isTaxonomy,
        targetTopicAlias: props.topic.alias,
        taxonomyAlias: props.topic.taxonomyAlias,
        topicTreeMap,
      });
    }, [dragContext.isDragging, props.topic, topicTreeMap]);

  const onClassifyTopicHandler = async (
    alias: string,
    params: LinkTopicParams,
    needRemove: boolean,
  ) => {
    try {
      await service.taxonomy.classifyFeedbackToTopic(alias, params);
      dragContext.setSelectedList([]);
      if (needRemove) {
        removeItem(params.feedbacks);
      }
      addToast(
        replaceFeedbackTerminology(
          'Feedbacks have been classified to the topic',
        ),
        'success',
      );
    } catch (e) {
      console.error(e);
      addToast(
        replaceFeedbackTerminology('Failed to classify feedbacks'),
        'fail',
      );
    }
  };

  const [{ isOver }, dropzone] = useDrop(
    () => ({
      accept: TAXONOMY_DND_ITEM,
      drop(item) {
        if (!dragContext.isDragging) return;
        if (!isValidDrop) return;
        const typedItem = item as FeedbackCardDragItem;
        onClassifyTopicHandler(
          props.topic.alias,
          {
            current_topic_id: dragContext.isDragging.isTaxonomy
              ? undefined
              : dragContext.isDragging.alias,
            feedbacks: typedItem.feedbacks,
            taxonomy_id: props.topic.taxonomyAlias,
          },
          dragContext.isDragging.forceRemove
            ? true
            : isValidDrop !== 'START_TOPIC_IS_PARENT_OF_TARGET_TOPIC',
        );
      },
      collect(monitor) {
        return {
          isOver: monitor.isOver(),
        };
      },
    }),
    [isValidDrop, dragContext.isDragging],
  );

  return (
    <React.Fragment>
      <Accordion
        onChange={() =>
          toggleTopic(props.topic.taxonomyAlias, props.topic.alias)
        }
        initialOpen={
          typeof props.topic.collapsed === 'undefined'
            ? false
            : !props.topic.collapsed
        }
        anchor={(isOpened) => (
          <Style
            a="center"
            ref={dropzone}
            focused={focused}
            isOver={isOver}
            isValidDrop={isValidDrop}
          >
            <Flex
              className="col title"
              a="center"
              style={{
                flex: 1,
                maxWidth: 1400,
                paddingLeft: offset,
              }}
              g={4}
            >
              <Flex className="handler-area" j="center" a="center">
                {childTopics && childTopics.length > 0 && (
                  <RightOutlined
                    style={{
                      fontSize: 16,
                      transition: 'all 0.2s',
                      rotate: !isOpened ? '0deg' : '90deg',
                      color: color.gray600,
                    }}
                  />
                )}
              </Flex>
              <VFlex g={6} f={1}>
                <Flex
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onClick(props.topic.alias);
                  }}
                  a="center"
                  className="select-area"
                  f={1}
                >
                  <Flex a="center" className="ticket-area">
                    {getIcon(props.topic.category, {
                      color: focused ? color.primary100 : color.gray700,
                    })}
                  </Flex>

                  <Flex className="title-area" g={12} f={1} a="center">
                    <TooltipV2 tooltipNode={props.topic.name}>
                      <Typo
                        type="label2"
                        weight={props.topic.level === 1 ? 'sb' : 'm'}
                        ellipsis
                        style={{ flex: 1 }}
                      >
                        {props.topic.name}
                      </Typo>
                    </TooltipV2>
                  </Flex>
                </Flex>
                {props.showPath && (
                  <Flex className="path-area" f={1}>
                    <TooltipV2
                      tooltipNode={taxonomyUtils
                        .getTopicPath({
                          ...props.topic,
                        })
                        .map((t) => t.label)
                        .join(' / ')}
                    >
                      <Typo
                        type="label2"
                        weight="r"
                        ellipsis
                        className="text-gray-600"
                        style={{ flex: 1 }}
                      >
                        {taxonomyUtils
                          .getTopicPath({
                            ...props.topic,
                          })
                          .map((t) => t.label)
                          .join(' / ')}
                      </Typo>
                    </TooltipV2>
                  </Flex>
                )}
              </VFlex>
            </Flex>
            {isPrediction ? (
              <Tooltip
                style={{
                  padding: 24,
                  backgroundColor: color.gray100,
                }}
                tooltipNode={
                  <PredictionStateDisplay
                    prediction_state={props.topic.prediction_state}
                    progress_rate={props.topic.progress_rate}
                  />
                }
              >
                <CenterFlex className="spinner">
                  <Spinner />
                </CenterFlex>
              </Tooltip>
            ) : (
              props.topic.category !== TOPIC_2_TYPE.SAVED_TOPIC && (
                <Tooltip
                  tooltipNode={
                    !taxonomyUtils.canCreateChildTopic(props.topic.level)
                      ? 'You cannot create more than 4 levels.'
                      : null
                  }
                >
                  <Flex className="hover-item">
                    <Menu
                      triggerNode={
                        <IconButton
                          tooltip="Add subtopic"
                          disabled={
                            !taxonomyUtils.canCreateChildTopic(
                              props.topic.level,
                            )
                          }
                          icon={
                            <PlusOutlined
                              style={{
                                transform: 'translate(-0.5px, -0.5px)',
                              }}
                            />
                          }
                          css={css`
                            background-color: transparent;
                            color: ${(props) => props.theme.colors.primary100};
                          `}
                          outlined={false}
                        />
                      }
                      options={[
                        {
                          label: 'Explore with AI',
                          onClick: () => openAiSuggestionModal(),
                          disabled:
                            childTopicType === TOPIC_2_TYPE.MANUAL ||
                            childTopicType === TOPIC_2_TYPE.RULE,
                        },
                        {
                          label: 'Create your own',
                          onClick: () => openCreateModal(),
                        },
                      ]}
                    />
                  </Flex>
                </Tooltip>
              )
            )}
          </Style>
        )}
        content={taxonomyUtils
          .topicSortFunc(childTopics, TopicTreeSortBy.TITLE, 'desc')
          .map((topic) => (
            <ShortTopicRow
              key={topic.alias}
              topic={topic}
              onClick={props.onClick}
              currentTopicAlias={props.currentTopicAlias}
              createTopicCallback={props.createTopicCallback}
            />
          ))}
      />
      <Modal
        status={createModalStatus}
        closeModal={() => {
          closeCreateModal();
          props.createTopicCallback?.(props.topic.alias);
        }}
      >
        <SelectTopicTypeModal
          type={childTopicType}
          target="topic"
          targetAlias={props.topic.alias}
          taxonomyAlias={props.topic.taxonomyAlias}
          targetName={props.topic.name}
        />
      </Modal>

      <Modal
        status={aiSuggestionModalStatus}
        closeModal={() => {
          closeAiSuggestionModal();
          props.createTopicCallback?.(props.topic.alias);
        }}
      >
        <AISuggestionModal
          target="topic"
          targetAlias={props.topic.alias}
          taxonomyAlias={props.topic.taxonomyAlias}
          targetName={props.topic.name}
        />
      </Modal>
    </React.Fragment>
  );
};

type StyleProps = {
  focused: boolean;
  isOver: boolean;
  isValidDrop: boolean | 'START_TOPIC_IS_PARENT_OF_TARGET_TOPIC';
};

const Style = styled(Flex)<StyleProps>`
  width: 100%;
  padding: 12px 0;
  padding-right: 8px;
  align-items: center;
  transition: all 0.2s;
  border-radius: ${(props) => props.theme.radius.token.lg}px;
  border: 1px solid transparent;
  position: relative;

  .spinner {
    top: 50%;
    right: 12px;
    transform: translateY(-50%);

    position: absolute;
  }
  .hover-item {
    position: relative;
    z-index: 2;
    opacity: 0;
    align-items: center;
    transition: all 0.2s;
  }

  ${({ focused, isOver, isValidDrop, theme }) => css`
    border-color: ${focused
      ? theme.colors.primary100
      : isOver && isValidDrop
        ? theme.colors.primary100
        : 'transparent'};
    background-color: ${focused || (isOver && isValidDrop)
      ? theme.colors.selected
      : 'transparent'};
    opacity: ${isValidDrop ? 1 : 0.5};
  `}

  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
    .hover-item {
      opacity: 1;
    }
  }

  .col {
    height: 100%;
    padding: 0px 16px;
    &.title {
      padding: 0;
      .handler-area {
        user-select: none;
        padding: 0px 2px;
        width: 28px;
        color: ${(props) => props.theme.colors.gray700};
        cursor: pointer;
      }
      .select-area {
        cursor: pointer;
        height: 100%;
      }
      .ticket-area {
        width: 16px;
      }
      .title-area {
        padding: 0 8px;
        flex: 1;
        color: ${(props) => props.theme.colors.gray800};
      }
    }
    &.feedback {
      ${SpanStyle({
        type: 'label2',
        weight: 'm',
      })}
      color: ${(props) => props.theme.colors.gray800};
    }
    &.type {
    }
    &.action {
    }
  }
`;

export default memo(ShortTopicRow);
